import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Star,
  Save,
  Check,
  AlertCircle,
  Pencil,
  RotateCcw,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_all_testimonials,
  save_update_testimonials,
  delete_testimonials,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

// ─── Constants ────────────────────────────────────────────────────────────────
// Backend model: primary_id, name, role, rating, message, flag
// No slot field — list-based, not slot-based

const EMPTY_FORM = {
  primary_id: null,
  name: "",
  role: "Verified Customer",
  rating: 5,
  message: "",
};

// ─── Star Rating Picker ───────────────────────────────────────────────────────
const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        className="transition-transform hover:scale-110"
      >
        <Star
          className={`h-5 w-5 transition-colors ${
            star <= value
              ? "fill-primary text-primary"
              : "fill-muted text-muted-foreground/30"
          }`}
        />
      </button>
    ))}
  </div>
);

// ─── Preview Card ─────────────────────────────────────────────────────────────
const PreviewCard = ({ data }) => (
  <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
    {/* Stars */}
    <div className="mb-4 flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`h-4 w-4 ${
            s <= (data.rating || 5)
              ? "fill-primary text-primary"
              : "fill-muted text-muted-foreground/20"
          }`}
        />
      ))}
    </div>

    {/* Message — backend field name is `message` */}
    <p className="mb-5 min-h-[60px] text-sm leading-relaxed text-muted-foreground">
      {data.message ? (
        `"${data.message}"`
      ) : (
        <span className="italic text-muted-foreground/50">
          Testimonial text will appear here...
        </span>
      )}
    </p>

    {/* Author */}
    <div className="flex items-center gap-3 border-t border-border pt-4">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-primary text-sm font-bold text-white">
        {data.name ? data.name.charAt(0).toUpperCase() : "?"}
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">
          {data.name || (
            <span className="italic text-muted-foreground/50">
              Customer Name
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {data.role || "Verified Customer"}
        </p>
      </div>
    </div>
  </div>
);

// ─── Testimonial Form ─────────────────────────────────────────────────────────
const TestimonialForm = ({ data, onChange, onReset, validateRef }) => {
  const [errors, setErrors] = useState({});

  // Expose validate fn to parent via ref
  useEffect(() => {
    if (validateRef) {
      validateRef.current = () => {
        const e = {};
        if (!data.name?.trim()) e.name = "Name is required";
        if (!data.message?.trim()) e.message = "Testimonial text is required";
        if ((data.message?.trim().length || 0) > 300)
          e.message = "Max 300 characters";
        setErrors(e);
        return Object.keys(e).length === 0;
      };
    }
  }, [data, validateRef]);

  const handleChange = (key, val) => {
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    onChange({ ...data, [key]: val });
  };

  return (
    <div className="space-y-4">
      {/* Name + Role row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-foreground">
            Customer Name <span className="text-destructive">*</span>
          </label>
          <Input
            placeholder="e.g. James Mitchell"
            value={data.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            className={errors.name ? "border-destructive" : ""}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-destructive">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-foreground">
            Role / Label
          </label>
          <Input
            placeholder="Verified Customer"
            value={data.role || ""}
            onChange={(e) => handleChange("role", e.target.value)}
          />
        </div>
      </div>

      {/* Rating */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-foreground">
          Star Rating
        </label>
        <StarPicker
          value={data.rating || 5}
          onChange={(r) => handleChange("rating", r)}
        />
      </div>

      {/* Message — maps to backend `message` CharField */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-foreground">
          Testimonial Text <span className="text-destructive">*</span>
        </label>
        <Textarea
          placeholder="Write what the customer said about your service..."
          rows={4}
          value={data.message || ""}
          onChange={(e) => handleChange("message", e.target.value)}
          className={errors.message ? "border-destructive" : ""}
        />
        <div className="mt-1 flex items-center justify-between">
          {errors.message ? (
            <p className="text-xs text-destructive">{errors.message}</p>
          ) : (
            <span />
          )}
          <p
            className={`text-[11px] ${
              (data.message?.length || 0) > 280
                ? "font-semibold text-warning"
                : "text-muted-foreground"
            }`}
          >
            {data.message?.length || 0} / 300
          </p>
        </div>
      </div>

      {/* Reset */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive"
        >
          <RotateCcw className="h-3 w-3" /> Reset form
        </button>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const TestimonialsManager = () => {
  const { toast } = useToast();

  const [testimonials, setTestimonials] = useState([]);
  const [activeId, setActiveId] = useState(null); // selected primary_id
  const [form, setForm] = useState(EMPTY_FORM);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(null); // primary_id being deleted
  const [savedId, setSavedId] = useState(null);

  const validateRef = useRef(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("flag", "1");

      const response = await server_post_data(get_all_testimonials, fd);

      if (!response.data.error) {
        const data = response.data.message.data_testimonials || [];
        setTestimonials(data);

        // Auto-select first record only on initial load
        if (data.length > 0 && activeId === null) {
          const first = data[0];
          setActiveId(first.primary_id);
          setForm({
            primary_id: first.primary_id,
            name: first.name || "",
            role: first.role || "Verified Customer",
            rating: first.rating || 5,
            message: first.message || "",
          });
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Select testimonial from list ────────────────────────────────────────
  const handleSelect = (item) => {
    setActiveId(item.primary_id);
    setForm({
      primary_id: item.primary_id,
      name: item.name || "",
      role: item.role || "Verified Customer",
      rating: item.rating || 5,
      message: item.message || "",
    });
  };

  // ─── New testimonial ─────────────────────────────────────────────────────
  const handleNew = () => {
    setActiveId(null);
    setForm(EMPTY_FORM);
  };

  // ─── Reset form ──────────────────────────────────────────────────────────
  const handleReset = () => {
    if (activeId) {
      const item = testimonials.find((t) => t.primary_id === activeId);
      if (item) {
        setForm({
          primary_id: item.primary_id,
          name: item.name || "",
          role: item.role || "Verified Customer",
          rating: item.rating || 5,
          message: item.message || "",
        });
      }
    } else {
      setForm(EMPTY_FORM);
    }
  };

  // ─── Save / Update ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (validateRef.current && !validateRef.current()) return;

    try {
      setShowLoaderAdmin(true);

      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("role", form.role || "Verified Customer");
      fd.append("rating", form.rating || 5);
      fd.append("message", form.message);
      fd.append("main_id", form.primary_id || "0"); // 0 = create, >0 = update

      const response = await server_post_data(save_update_testimonials, fd);

      if (!response.data.error) {
        toast({
          title: "Success",
          description: response.data.message,
        });

        setSavedId(form.primary_id || "new");
        setTimeout(() => setSavedId(null), 3000);

        // After create → clear form; after update → keep selection
        if (!form.primary_id) {
          setActiveId(null);
          setForm(EMPTY_FORM);
        }

        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async (primary_id) => {
    try {
      setDeleteLoader(primary_id);

      const fd = new FormData();
      fd.append("id_for_delete", primary_id);
      fd.append("for_status_final", "d");

      const response = await server_post_data(delete_testimonials, fd);

      if (!response.data.error) {
        toast({
          title: "Deleted",
          description: response.data.message,
        });

        // If deleted item was open in form, clear it
        if (activeId === primary_id) {
          setActiveId(null);
          setForm(EMPTY_FORM);
        }

        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(null);
    }
  };

  // ─── Derived ──────────────────────────────────────────────────────────────
  const isNew = !form.primary_id;
  const isSaved = savedId === (form.primary_id || "new");

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            Testimonials Manager
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage customer reviews displayed on your website.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-2 rounded-xl"
          onClick={handleNew}
        >
          <Plus className="h-4 w-4" /> Add New
        </Button>
      </div>

      {/* Main grid — list left, form + preview right */}
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* ── Left: Testimonials List ──────────────────────────────────── */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            All Testimonials ({testimonials.length})
          </p>

          {/* Loading state */}
          {fetchLoader && (
            <div className="flex items-center justify-center rounded-2xl border border-border bg-card py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {!fetchLoader && testimonials.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-center">
              <p className="text-sm text-muted-foreground">
                No testimonials yet.
              </p>
              <button
                onClick={handleNew}
                className="mt-2 text-xs font-semibold text-primary hover:underline"
              >
                Add your first one
              </button>
            </div>
          )}

          {/* List */}
          {!fetchLoader &&
            testimonials.map((item) => {
              const isActive = activeId === item.primary_id;
              return (
                <div
                  key={item.primary_id}
                  className={`group relative cursor-pointer rounded-2xl border p-3 transition-all ${
                    isActive
                      ? "border-primary bg-primary/5 shadow-glow"
                      : "border-border bg-card hover:border-primary/30"
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gradient-primary">
                      <Pencil className="h-2.5 w-2.5 text-white" />
                    </span>
                  )}

                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-sm font-bold text-white">
                      {item.name ? item.name.charAt(0).toUpperCase() : "?"}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-foreground">
                        {item.name || "—"}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {item.message || "No text"}
                      </p>
                      {/* Stars */}
                      <div className="mt-1 flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`h-2.5 w-2.5 ${
                              s <= (item.rating || 5)
                                ? "fill-primary text-primary"
                                : "fill-muted text-muted-foreground/20"
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Delete */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.primary_id);
                      }}
                      disabled={deleteLoader === item.primary_id}
                      className="shrink-0 rounded-lg p-1 text-muted-foreground opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                    >
                      {deleteLoader === item.primary_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        {/* ── Right: Form + Preview ─────────────────────────────────────── */}
        <div className="space-y-6">
          {/* Form Card */}
          <Card>
            <div className="border-b border-border px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-primary text-xs font-bold text-white">
                  {isNew ? "+" : form.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">
                    {isNew
                      ? "Add New Testimonial"
                      : `Editing — ${form.name || "Untitled"}`}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {isNew
                      ? "Fill in the details and save"
                      : `ID: ${form.primary_id}`}
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <TestimonialForm
                key={activeId ?? "new"}
                data={form}
                onChange={setForm}
                onReset={handleReset}
                validateRef={validateRef}
              />
            </CardContent>
          </Card>

          {/* Info banner for new entry */}
          {isNew && (
            <div className="flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-xs text-muted-foreground">
                Fill in{" "}
                <span className="font-semibold text-foreground">
                  Customer Name
                </span>{" "}
                and{" "}
                <span className="font-semibold text-foreground">
                  Testimonial Text
                </span>{" "}
                to save a new review.
              </p>
            </div>
          )}

          {/* Live Preview */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Live Preview
            </p>
            <PreviewCard data={form} />
          </div>

          {/* Save Button */}
          <div className="flex justify-end border-t border-border pt-4">
            <Button
              size="lg"
              disabled={showLoaderAdmin}
              className={`gap-2 rounded-xl transition-all ${
                isSaved ? "bg-success hover:bg-success" : ""
              }`}
              onClick={handleSave}
            >
              {showLoaderAdmin ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                </>
              ) : isSaved ? (
                <>
                  <Check className="h-4 w-4" /> Saved!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isNew ? "Save Testimonial" : "Update Testimonial"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsManager;
